<template>
    <div ref="mapContainer" class="mt-4" style="position: relative; height: 300px; width: 100%; border: 1px solid #ccc; border-radius: 4px;">
        <div style="position: absolute; bottom: 10px; left: 10px; z-index: 1000; background-color: white; padding: 5px 10px; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">
            Pick station location on the map:
        </div>
    </div>
</template>

<script setup>
/**
 * Migrated from Options API to Composition API.
 * - Data properties are now refs.
 * - Methods are now functions in setup.
 * - Lifecycle hooks use onMounted/onBeforeUnmount.
 * - Props and emits use defineProps/defineEmits.
 * - Map initialization and cleanup handled in setup.
 * - Watches and marker interactions preserved.
 * - All template and styles unchanged.
 */

import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Initialize Leaflet icon paths (unchanged)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Props and emits
const props = defineProps({
    initialCenter: {
        type: Array,
        default: () => [40.429, -4.249]
    },
    initialZoom: {
        type: Number,
        default: 3
    },
    disabled: {
        type: Boolean,
        default: false
    }
});
const emit = defineEmits(['location-updated']);

// Refs for DOM and map objects
const mapContainer = ref(null);
const map = ref(null);
const marker = ref(null);

// Map initialization
const initMap = () => {
    if (map.value) map.value.remove();
    const mapElement = mapContainer.value;
    if (!mapElement) return;

    map.value = L.map(mapElement).setView(props.initialCenter, props.initialZoom);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(map.value);

    marker.value = L.marker(props.initialCenter, { draggable: !props.disabled }).addTo(map.value);
    updateFormFields(marker.value.getLatLng());

    // Force map to recalculate its size after initialization
    setTimeout(() => {
        map.value.invalidateSize();
    }, 100);

    if (!props.disabled) {
        map.value.on('click', (e) => {
            marker.value.setLatLng(e.latlng);
            updateFormFields(e.latlng);
        });
        marker.value.on('dragend', (e) => {
            updateFormFields(e.target.getLatLng());
        });
    }
};

// Normalize longitude to [-180, 180] range
const normalizeLongitude = (lng) => {
    lng = ((lng + 180) % 360) - 180;
    if (lng < -180) lng += 360;
    return lng;
};

// Emit location to parent
const updateFormFields = (latlng) => {
    const normalizedLng = normalizeLongitude(latlng.lng);
    emit('location-updated', {
        lat: latlng.lat.toFixed(3),
        lng: normalizedLng.toFixed(3)
    });
    // Remove marker position update to keep visual position as user placed it
};

// Cleanup map on unmount
const cleanup = () => {
    if (map.value) {
        map.value.remove();
        map.value = null;
        marker.value = null;
    }
};

// Watch for disabled prop changes to enable/disable marker dragging
watch(
    () => props.disabled,
    (newVal) => {
        if (marker.value) {
            marker.value.setLatLng(marker.value.getLatLng());
            if (marker.value.dragging) {
                marker.value.dragging[newVal ? 'disable' : 'enable']();
            }
        }
    }
);

// Lifecycle hooks
onMounted(() => {
    nextTick(() => {
        initMap();
    });
});
onBeforeUnmount(() => {
    cleanup();
});
</script>